import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Table, Button, Spinner, Badge, Modal, Form, Alert, Nav } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { FaCheck, FaTimes, FaTrash, FaEye, FaRedo, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { formatTimeAgo } from '../../../utils/communityUtils';

export default function AdminModerationQueue() {
  const { token } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState(null);
  
  const [activeTab, setActiveTab] = useState('discussions_needs_review');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pageHistory, setPageHistory] = useState([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const cursor = pageHistory[currentPage];
      let url = `${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/moderation?tab=${activeTab}&status=${statusFilter}&limit=20`;
      if (cursor) url += `&startAfter=${cursor}`;
      
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setQueue(data.items || []);
        setHasMore((data.items || []).length === 20);
      }
    } catch (error) {
      console.error("Error fetching moderation queue", error);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, statusFilter, currentPage, pageHistory]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleTabChange = (k) => {
    setActiveTab(k);
    setStatusFilter('pending');
    setPageHistory([null]);
    setCurrentPage(0);
  };

  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPageHistory([null]);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    const lastItem = queue[queue.length - 1];
    if (!lastItem) return;
    const cursor = statusFilter === 'all' ? lastItem.tab_time : lastItem.tab_status_time;
    setPageHistory(prev => {
      const next = [...prev];
      next[currentPage + 1] = cursor;
      return next;
    });
    setCurrentPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 0) setCurrentPage(prev => prev - 1);
  };

  const handleAction = async (id, action, reason) => {
    try {
      setActionLoading(true);
      setActionError(null);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/moderation/${id}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action, reason: reason || undefined })
      });
      const data = await res.json();
      if (data.success) {
        setQueue(prev => prev.map(item => {
          if (item.id === id) {
            let newStatus = action === 'approve' || action === 'mark_reviewed' ? 'approved' : action === 'delete' ? 'deleted' : 'rejected';
            return { ...item, status: newStatus };
          }
          return item;
        }).filter(item => statusFilter === 'all' || item.status === statusFilter));
        
        setShowModal(false);
        setShowRejectModal(false);
        setRejectReason('');
        setRejectTarget(null);
      } else {
        setActionError(data.error || "Action failed");
      }
    } catch (error) {
      console.error("Error performing moderation action", error);
      setActionError("Network error. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (item) => {
    setRejectTarget(item);
    setRejectReason('');
    setActionError(null);
    setShowModal(false);
    setShowRejectModal(true);
  };

  const viewDetails = (item) => {
    setSelectedItem(item);
    setActionError(null);
    setShowModal(true);
  };

  const getFlagBadgeVariant = (flagType) => {
    if (flagType === 'needs_review') return 'success';
    if (flagType === 'resubmission') return 'info';
    if (flagType === 'new_post' || flagType === 'new_reply') return 'secondary';
    return 'warning'; // flagged by ai
  };

  return (
    <div className="py-4 community-admin-container">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2>Moderation Queue</h2>
          <p className="text-muted mb-0 small">Review and manage the platform's content stream.</p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={fetchQueue}>
          <FaRedo className="me-1" /> Refresh
        </Button>
      </div>

      {actionError && (
        <Alert variant="danger" dismissible onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white pt-3 pb-0">
          <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabChange}>
            <Nav.Item>
              <Nav.Link eventKey="discussions_needs_review" className="text-dark fw-bold">
                Discussions: Needs Review
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="discussions_flagged" className="text-danger fw-bold">
                Discussions: Flagged by AI
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="replies_needs_review" className="text-dark fw-bold">
                Replies: Needs Review
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="replies_flagged" className="text-danger fw-bold">
                Replies: Flagged by AI
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="bg-light px-4 py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center border-bottom">
            <div className="text-muted small mb-2 mb-md-0 fw-semibold">
              {statusFilter === 'pending' && <span className="text-danger">● </span>}
              Showing {statusFilter === 'all' ? 'all history' : `items marked as ${statusFilter === 'pending' ? (activeTab.includes('needs_review') ? 'needs review' : 'pending AI flag') : statusFilter}`}
            </div>
            <Form.Select 
              size="sm" 
              value={statusFilter} 
              onChange={handleFilterChange} 
              style={{ width: 'auto', minWidth: '220px' }}
              className="shadow-sm border-0"
            >
              {activeTab.includes('needs_review') ? (
                <>
                  <option value="pending">✓ Needs Review (Action Req.)</option>
                  <option value="approved">Reviewed / Approved</option>
                  <option value="all">All Items</option>
                </>
              ) : (
                <>
                  <option value="pending">✓ Pending AI Flag (Action Req.)</option>
                  <option value="approved">Approved / Made Public</option>
                  <option value="rejected">Rejected</option>
                  <option value="all">All History</option>
                </>
              )}
            </Form.Select>
          </div>
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Status</th>
                <th>Author</th>
                <th>Title / Content</th>
                <th>AI Flag / Reason</th>
                <th>Submitted</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && queue.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <Spinner animation="border" variant="warning" />
                  </td>
                </tr>
              ) : queue.length > 0 ? (
                queue.map(item => (
                  <tr key={item.id} className={item.status === 'rejected' || item.status === 'deleted' ? 'opacity-50' : ''}>
                    <td>
                      <Badge bg={item.status === 'approved' ? 'success' : item.status === 'pending' ? 'warning' : 'danger'}>
                        {item.status.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="small text-muted">{item.authorName || item.authorId}</td>
                    <td style={{ maxWidth: '280px' }}>
                      {item.contentTitle && (
                        <div className="fw-bold text-truncate small">{item.contentTitle}</div>
                      )}
                      <div className="text-truncate text-muted small">{item.contentSnippet}</div>
                    </td>
                    <td>
                      {item.flagType && (
                        <Badge bg={getFlagBadgeVariant(item.flagType)} text={item.flagType === 'new_post' || item.flagType === 'needs_review' ? 'light' : 'dark'}>
                          {item.flagType}
                        </Badge>
                      )}
                      {item.flagReason && (
                        <div className="text-muted small mt-1" style={{ maxWidth: '200px', whiteSpace: 'normal' }}>
                          {item.flagReason}
                        </div>
                      )}
                    </td>
                    <td className="small text-muted">{formatTimeAgo(item.createdAt)}</td>
                    <td className="text-end">
                      <Button variant="outline-info" size="sm" className="me-1 mb-1" onClick={() => viewDetails(item)}>
                        <FaEye />
                      </Button>
                      
                      {item.status !== 'approved' && (
                        <Button
                          variant="success"
                          size="sm"
                          className="me-1 mb-1"
                          onClick={() => handleAction(item.id, item.flagType === 'needs_review' ? 'mark_reviewed' : 'approve')}
                          disabled={actionLoading}
                          title="Approve / Make Public"
                        >
                          <FaCheck /> {item.flagType === 'needs_review' ? 'Reviewed' : 'Approve'}
                        </Button>
                      )}

                      {item.status !== 'rejected' && item.status !== 'deleted' && (
                        <Button
                          variant="warning"
                          size="sm"
                          className="mb-1"
                          onClick={() => openRejectModal(item)}
                          disabled={actionLoading}
                          title="Reject — ask author to edit"
                        >
                          <FaTimes /> Reject
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    {loading ? <Spinner animation="border" size="sm" /> : 'No items found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          
          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
            <span className="text-muted small">
              Page {currentPage + 1}
            </span>
            <div>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="me-2" 
                onClick={handlePrevPage} 
                disabled={currentPage === 0 || loading}
              >
                <FaChevronLeft className="me-1" /> Previous
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={handleNextPage} 
                disabled={!hasMore || loading}
              >
                Next <FaChevronRight className="ms-1" />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedItem?.type === 'discussion' ? 'Discussion Details' : 'Reply Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              <Row className="mb-3">
                <Col sm={3} className="text-muted">Author</Col>
                <Col className="fw-bold">{selectedItem.authorName || selectedItem.authorId}</Col>
              </Row>
              <Row className="mb-3">
                <Col sm={3} className="text-muted">Title</Col>
                <Col>{selectedItem.contentTitle || 'N/A'}</Col>
              </Row>
              <Row className="mb-3">
                <Col sm={3} className="text-muted">Status</Col>
                <Col>
                  <Badge bg={selectedItem.status === 'approved' ? 'success' : selectedItem.status === 'pending' ? 'warning' : 'danger'}>
                    {selectedItem.status.toUpperCase()}
                  </Badge>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm={3} className="text-muted">AI Flag</Col>
                <Col>
                  <Badge bg={getFlagBadgeVariant(selectedItem.flagType)}>
                    {selectedItem.flagType || 'new_post'}
                  </Badge>
                  {selectedItem.flagReason && (
                    <p className="text-muted small mt-1 mb-0">{selectedItem.flagReason}</p>
                  )}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm={3} className="text-muted">Submitted</Col>
                <Col>{formatTimeAgo(selectedItem.createdAt)}</Col>
              </Row>
              <div className="p-3 bg-light rounded border">
                <strong>Content Preview:</strong>
                <p className="mb-0 mt-2 text-muted">{selectedItem.contentSnippet}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-danger"
            onClick={() => handleAction(selectedItem.id, 'delete')}
            disabled={actionLoading || selectedItem?.status === 'deleted'}
          >
            <FaTrash className="me-1" /> Delete Permanently
          </Button>
          <div>
            <Button variant="secondary" onClick={() => setShowModal(false)} className="me-2">
              Close
            </Button>
            {selectedItem?.status !== 'rejected' && selectedItem?.status !== 'deleted' && (
              <Button
                variant="warning"
                onClick={() => openRejectModal(selectedItem)}
                disabled={actionLoading}
                className="me-2"
              >
                <FaTimes className="me-1" /> Reject
              </Button>
            )}
            {selectedItem?.status !== 'approved' && (
              <Button
                variant="success"
                onClick={() => handleAction(selectedItem.id, selectedItem?.flagType === 'needs_review' ? 'mark_reviewed' : 'approve')}
                disabled={actionLoading}
              >
                {actionLoading ? <Spinner size="sm" className="me-1" /> : <FaCheck className="me-1" />}
                {selectedItem?.flagType === 'needs_review' ? 'Mark Reviewed' : 'Approve & Publish'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* Reject with Reason Modal */}
      <Modal show={showRejectModal} onHide={() => { setShowRejectModal(false); setRejectReason(''); setActionError(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Post</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Rejecting this post will notify the author and allow them to edit and resubmit it. 
            Please provide a clear reason so they know what to fix.
          </p>
          <Form.Group>
            <Form.Label className="fw-bold">Reason for Rejection <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder='e.g. "Please remove the external contact information."'
              maxLength={500}
            />
            <Form.Text className="text-muted">{rejectReason.length}/500</Form.Text>
          </Form.Group>
          {actionError && <Alert variant="danger" className="mt-3">{actionError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowRejectModal(false); setRejectReason(''); setActionError(null); }}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleAction(rejectTarget.id, 'reject', rejectReason)}
            disabled={actionLoading || !rejectReason.trim()}
          >
            {actionLoading ? <Spinner size="sm" className="me-1" /> : <FaTimes className="me-1" />}
            Send Rejection
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
